// Composer Mode chip (Autonomous / Plan) — restored 2026-05-18.
//
// Originally added in Sprint UI-10, removed in PR #243 ("never changed
// mid-conversation"), restored 2026-05-18 because both the new-agent
// flow and mid-chat switches are legitimate: the mode is picked up-front
// per conversation, but the founder may still flip it later (the change
// persists for the rest of that conversation via agents.permissionMode
// + respawn). The respawn is why ContinueComposer wraps onChange in a
// confirm dialog; NewComposer renders the chip raw (nothing to respawn).
//
// Sprint mode-trust-mode-truth-up (2026-06-04): the picker no longer
// exposes 'auto'. 'auto' is kept as a runtime alias of 'agent' (same
// CLI flag + same hook) so legacy chips that still carry mode='auto'
// render the same label/icon. The MODE_ICON map keeps the 'auto' key
// for that reason.
//
// DS.6 (sprint aos-ds-composer, §2 D3/D4 branch B) — the shadcn
// `DropdownMenu` + ghost `Button` are gone. The chip IS a real
// `@ds/data-display/Chip variant="filter"` <button> (native `disabled`, the
// testid and both ARIA attributes on the node that activates), handed its
// trigger props by `ds/DsChipMenu`, which owns the menu surface and the
// keyboard model. `selected` is never passed: it would emit the piece's own
// `check` Material ligature (Chip.tsx:47 → :70). The chevron rides `trailing`
// and the open state is `aria-expanded` alone. i18n strings live at
// modeChip.{agent,plan,auto} + *Desc; reused from SettingsPage (single source
// of truth for the labels).

import { useState } from 'react';
import Chip from '@ds/data-display/Chip';
import type { PermissionMode } from '../types';
import { useTranslation } from '../i18n';
import { Icon, type IconName } from './atoms/Icon';
import { DsChipMenu, DsChipMenuRow } from './ds/DsChipMenu';

export interface ModeChipProps {
  mode: PermissionMode;
  onChange: (next: PermissionMode) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

// MODE_ICON keeps the 'auto' key so legacy chips that still carry
// mode='auto' (post-Sprint-mode-trust-mode-truth-up alias) render the
// same Infinity icon as 'agent'.
const MODE_ICON: Record<PermissionMode, IconName> = {
  agent: 'Infinity',
  plan: 'BookOpen',
  auto: 'Infinity',
};

// Ordered to match SettingsPage. 'auto' is intentionally NOT in this
// list — it is a runtime alias of 'agent' and is hidden from the picker.
const MODES: { value: PermissionMode; icon: IconName }[] = [
  { value: 'agent', icon: 'Infinity' },
  { value: 'plan', icon: 'BookOpen' },
];

export function ModeChip({ mode, onChange, disabled, size = 'sm' }: ModeChipProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = t(`modeChip.${mode}` as const);

  return (
    <DsChipMenu
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      data-testid="mode-chip"
      renderTrigger={(trigger) => (
        <Chip
          {...trigger}
          variant="filter"
          size={size === 'md' ? 'sm' : 'xs'}
          label={label}
          icon={<Icon name={MODE_ICON[mode]} size={12} aria-hidden />}
          trailing={<Icon name="ChevronDown" size={10} aria-hidden />}
          aria-label={t('modeChip.modeAriaLabel', { mode: label })}
          data-mode={mode}
        />
      )}
      renderMenu={(menu) => (
        <div
          {...menu}
          aria-label={t('modeChip.modeAriaLabel', { mode: label })}
          data-testid="mode-popover"
        >
          {MODES.map((m) => {
            const itemLabel = t(`modeChip.${m.value}` as const);
            const desc = t(`modeChip.${m.value}Desc` as const);
            return (
              <DsChipMenuRow
                key={m.value}
                onSelect={() => onChange(m.value)}
                data-testid={`mode-option-${m.value}`}
              >
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <Icon
                    name={m.icon}
                    size={14}
                    className="shrink-0 mt-0.5 text-muted-foreground"
                    aria-hidden
                  />
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="text-sm font-medium text-foreground">{itemLabel}</span>
                    <span className="text-xs text-muted-foreground">{desc}</span>
                  </span>
                </span>
              </DsChipMenuRow>
            );
          })}
        </div>
      )}
    />
  );
}
